import { useCallback, useRef, useState } from 'react';

/** Holding a button to talk.
 *
 *  Everything awkward about recording on a phone lives here so the component
 *  above can just render. The awkward parts, in order of how much trouble they
 *  cause:
 *
 *  iOS records AAC inside MP4 and nothing else until 18.4, so the format is
 *  chosen by asking rather than assumed. The AudioContext starts suspended and
 *  only a real user gesture may resume it, so start() must be called straight
 *  out of the pointer handler and not after an await. And the microphone stays
 *  reserved — with the orange dot lit — until every track is stopped, so that
 *  happens on every path out of here, including the cancelled one. */

export type Phase = 'idle' | 'asking' | 'recording' | 'done' | 'denied' | 'unsupported';

const FORMATS = [
  'audio/mp4',                 // what iOS actually produces, 14.5+
  'audio/webm;codecs=opus',    // iOS 18.4+, and everywhere else
  'audio/webm',
];

const pickFormat = () =>
  (typeof MediaRecorder !== 'undefined'
    && FORMATS.find((t) => MediaRecorder.isTypeSupported(t))) || '';

export const recordingSupported = () =>
  typeof MediaRecorder !== 'undefined'
  && typeof navigator !== 'undefined'
  && !!navigator.mediaDevices?.getUserMedia;

export interface Recording { blob: Blob; seconds: number }

export function useRecorder() {
  const [phase, setPhase] = useState<Phase>('idle');
  const [seconds, setSeconds] = useState(0);
  /** 0..1 per bar, newest last — what the waveform draws. */
  const [levels, setLevels] = useState<number[]>([]);

  const stream = useRef<MediaStream | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const ctx = useRef<AudioContext | null>(null);
  const raf = useRef(0);
  const startedAt = useRef(0);
  const settle = useRef<((r: Recording | null) => void) | null>(null);

  /** Release the microphone. Until every track is stopped the phone keeps the
   *  recording indicator lit, which is alarming and entirely our fault. */
  const release = useCallback(() => {
    cancelAnimationFrame(raf.current);
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    void ctx.current?.close().catch(() => {});
    ctx.current = null;
  }, []);

  const start = useCallback(async () => {
    if (!recordingSupported()) { setPhase('unsupported'); return false; }
    setPhase('asking');
    setLevels([]);
    setSeconds(0);
    let media: MediaStream;
    try {
      media = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch {
      setPhase('denied');
      return false;
    }
    stream.current = media;
    chunks.current = [];

    const mimeType = pickFormat();
    const rec = new MediaRecorder(media, mimeType ? { mimeType } : undefined);
    recorder.current = rec;
    rec.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
    rec.onstop = () => {
      const blob = new Blob(chunks.current, { type: rec.mimeType || mimeType || 'audio/mp4' });
      const took = (Date.now() - startedAt.current) / 1000;
      release();
      settle.current?.(blob.size ? { blob, seconds: took } : null);
      settle.current = null;
    };
    rec.start();
    startedAt.current = Date.now();
    setPhase('recording');

    // The analyser reads the same stream the recorder is writing; both can hold
    // it at once. It is never connected to the output — that would echo.
    try {
      const audio = new AudioContext();
      ctx.current = audio;
      if (audio.state === 'suspended') void audio.resume();
      const analyser = audio.createAnalyser();
      analyser.fftSize = 256;
      audio.createMediaStreamSource(media).connect(analyser);
      const buf = new Uint8Array(analyser.fftSize);
      let last = 0;
      const tick = () => {
        raf.current = requestAnimationFrame(tick);
        const now = performance.now();
        setSeconds((Date.now() - startedAt.current) / 1000);
        if (now - last < 55) return;                 // ~18 bars a second
        last = now;
        analyser.getByteTimeDomainData(buf);
        let peak = 0;
        for (const v of buf) peak = Math.max(peak, Math.abs(v - 128) / 128);
        setLevels((ls) => [...ls.slice(-63), Math.min(1, peak * 1.6)]);
      };
      raf.current = requestAnimationFrame(tick);
    } catch {
      // No waveform is a poorer button, not a broken one.
    }
    return true;
  }, [release]);

  /** Stop and hand back what was captured. */
  const stop = useCallback(() => new Promise<Recording | null>((resolve) => {
    const rec = recorder.current;
    if (!rec || rec.state === 'inactive') { release(); setPhase('idle'); resolve(null); return; }
    settle.current = (r) => { setPhase('done'); resolve(r); };
    rec.stop();
  }), [release]);

  /** Stop and throw it away — the slide-to-cancel path, and the one that runs
   *  if the app is put in the background mid-sentence. */
  const cancel = useCallback(() => {
    const rec = recorder.current;
    settle.current = null;
    if (rec && rec.state !== 'inactive') { rec.onstop = null; rec.stop(); }
    chunks.current = [];
    release();
    setPhase('idle');
    setLevels([]);
    setSeconds(0);
  }, [release]);

  const reset = useCallback(() => { setPhase('idle'); setLevels([]); setSeconds(0); }, []);

  return { phase, seconds, levels, start, stop, cancel, reset };
}
